// services/live/liveUnread — wires the unread store (services/unreadStore.ts)
// to the real world, and exposes the two React hooks the app uses:
//
//   useLiveUnreadSummary(rooms, userId)  → rows for the badges, or NULL when
//       today's path must run (flag off, not Supabase, migration 0076 missing,
//       or the first read failed). services/roomUnreadCounts.ts calls it.
//   useLiveUnreadRoom(...)               → the open room tells the store what is
//       on screen. components/RoomChat.tsx calls it.
//
// With FEATURES.liveV2Unread off both hooks are constant no-ops that use no
// React state, open no channel and make no request — the flag is a build-time
// constant, so which implementation a build carries never changes at run time.
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { FEATURES } from '../features';
import { USE_SUPABASE, supabase } from '../supabaseClient';
import { isMissingSchema, type RoomUnreadRow } from '../roomActionsParity';
import { getRoomMutes, subscribeRoomMutes, type RoomMuteMap } from '../roomMuteState';
import {
  createUnreadStore, createBrowserCrossTab, cursorIsoEndOfMs,
  type UnreadStore, type UnreadTruthRow,
} from '../unreadStore';
import { joinUserChannel, onUserEvent, onUserChannelJoin, isUserChannelJoined } from './userChannel';

export const LIVE_UNREAD_ON = FEATURES.liveV2Unread && USE_SUPABASE;

let store: UnreadStore | null = null;

/** The app-wide store (created on first use; never when the flag is off). */
export function liveUnreadStore(): UnreadStore {
  if (store) return store;
  store = createUnreadStore({
    fetchTruth: async () => {
      const { data, error } = await supabase().rpc('my_room_unread_counters');
      if (error) throw error;
      return (data || []) as UnreadTruthRow[];
    },
    markRead: async (roomId, cursorMs) => {
      const { error } = await supabase().rpc('mark_room_read', {
        p_room_id: roomId,
        p_at: cursorIsoEndOfMs(cursorMs),
      });
      if (error) throw error;
    },
    isMissingSchema,
    crossTab: createBrowserCrossTab(),
    isVisible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
    channelLive: isUserChannelJoined,
  });
  return store;
}

// ── one session per tab, ref-counted across the surfaces that mount the hook ──
let sessionUser: string | null = null;
let sessionRefs = 0;
let sessionOff: (() => void) | null = null;

function applyMuteDiff(prev: RoomMuteMap, next: RoomMuteMap): void {
  const s = liveUnreadStore();
  for (const id of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    if (prev[id] !== next[id]) s.setMuted(id, typeof next[id] === 'number' ? next[id] : null);
  }
}

function openSession(userId: string): void {
  const s = liveUnreadStore();
  const offs: Array<() => void> = [];
  offs.push(onUserEvent('unread_state', (p) => s.onHint(p)));
  offs.push(onUserEvent('room_removed', (p) => s.onRoomRemoved(p)));
  offs.push(onUserChannelJoin(() => s.wake()));
  offs.push(joinUserChannel(userId));
  s.start(userId);

  // This tab muting / unmuting a room shows at once; the truth read carries
  // muted_until for everything else.
  let lastMutes = getRoomMutes();
  offs.push(subscribeRoomMutes((m) => { applyMuteDiff(lastMutes, m); lastMutes = m; }));

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const onOnline = () => s.wake();
    const onVisible = () => { if (document.visibilityState !== 'hidden') s.wake(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    offs.push(() => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    });
  }
  sessionOff = () => {
    for (const off of offs.reverse()) { try { off(); } catch { /* ignore */ } }
    s.stop();
  };
}

function holdSession(userId: string): () => void {
  if (sessionUser !== userId) {
    if (sessionOff) sessionOff();
    sessionOff = null;
    sessionUser = userId;
    sessionRefs = 0;
    openSession(userId);
  }
  sessionRefs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (sessionUser !== userId) return;
    sessionRefs -= 1;
    if (sessionRefs > 0) return;
    // React StrictMode and route changes unmount/remount in the same tick;
    // closing the channel for that would be a join per navigation.
    setTimeout(() => {
      if (sessionRefs > 0 || sessionUser !== userId) return;
      if (sessionOff) sessionOff();
      sessionOff = null;
      sessionUser = null;
    }, 0);
  };
}

const NO_ROWS: Record<string, RoomUnreadRow> = Object.freeze({}) as Record<string, RoomUnreadRow>;
const subscribeNoop = () => () => {};

function useLiveUnreadSummaryOn(
  rooms: Array<{ id: string }>,
  userId: string | null | undefined,
): Record<string, RoomUnreadRow> | null {
  const s = liveUnreadStore();
  useEffect(() => {
    if (!userId) return;
    return holdSession(userId);
  }, [userId]);

  // Today's RPC re-runs whenever `rooms` republishes. With hints flowing that
  // is unnecessary; without them the store re-reads on the same trigger.
  useEffect(() => {
    if (userId) s.noteRoomsChanged();
  }, [rooms, userId, s]);

  const all = useSyncExternalStore(userId ? s.subscribe : subscribeNoop, s.getRows, s.getRows);
  const status = useSyncExternalStore(userId ? s.subscribe : subscribeNoop, s.status, s.status);

  const mine = useMemo(() => {
    // Only rooms this surface knows about: a stray counter row (a room the list
    // has not loaded, or no longer shows) must not inflate a total.
    const out: Record<string, RoomUnreadRow> = {};
    for (const r of rooms) if (all[r.id]) out[r.id] = all[r.id];
    return out;
  }, [all, rooms]);

  if (!userId) return null;
  if (status === 'ready') return mine;
  // First read still in flight: show nothing yet rather than ALSO firing
  // today's RPC. If it fails, status turns `unavailable` and today's path runs.
  // (`idle` with a user = the very first render, before the effect above has
  // started the store — the same moment.)
  if (status === 'loading' || status === 'idle') return NO_ROWS;
  return null;
}

/** Rows for the badges, or null = "run today's path". */
export const useLiveUnreadSummary: (
  rooms: Array<{ id: string }>,
  userId: string | null | undefined,
) => Record<string, RoomUnreadRow> | null = LIVE_UNREAD_ON ? useLiveUnreadSummaryOn : () => null;

export interface LiveUnreadRoomInput {
  /** Tab visible AND window focused (RoomChat's `tabVisible`). */
  visible: boolean;
  atBottom: boolean;
  /** `created_at` (ms) of the newest message on screen — a SERVER timestamp. */
  newestMs: number;
  /** False holds the cursor write back (until the unread-divider anchor is read). */
  canWrite: boolean;
}

function useLiveUnreadRoomOn(
  roomId: string | null | undefined,
  userId: string | null | undefined,
  input: LiveUnreadRoomInput,
): void {
  const { visible, atBottom, newestMs, canWrite } = input;
  useEffect(() => {
    if (!roomId || !userId) return;
    liveUnreadStore().reportActive({ roomId, visible, atBottom, newestMs, canWrite });
  }, [roomId, userId, visible, atBottom, newestMs, canWrite]);
  useEffect(() => {
    if (!roomId || !userId) return;
    return () => liveUnreadStore().clearActive(roomId);
  }, [roomId, userId]);
}

/** The open room reports what is on screen. No-op when the flag is off. */
export const useLiveUnreadRoom: (
  roomId: string | null | undefined,
  userId: string | null | undefined,
  input: LiveUnreadRoomInput,
) => void = LIVE_UNREAD_ON ? useLiveUnreadRoomOn : () => {};

/** Test seam. */
export function _resetLiveUnread(): void {
  if (sessionOff) sessionOff();
  sessionOff = null;
  sessionUser = null;
  sessionRefs = 0;
  store = null;
}
